import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  AuthContext,
  type AuthContextValue,
  type AuthLoginOptions,
  type AuthMePlayer,
  type AuthOrganization,
  type AuthState,
} from '@/app/providers/auth-context'
import { fetchAuthProfile } from '@/app/providers/auth-profile'
import { passwordSessionDoors } from '@/app/providers/passwordSessionDoors'
import { useAuthPermissions } from '@/app/providers/useAuthPermissions'
import { logoutSession as logoutSessionApi, refreshSession as refreshSessionApi } from '@/features/auth/api'
import { exchangeCodeForTokens, startSignInFlow } from '@/lib/auth/oauth'
import { clearStoredTokens, hasActiveAccessToken, readStoredTokens, type SessionTokens, writeStoredTokens } from '@/lib/auth/tokenStore'

type AuthProviderProps = {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [tokens, setTokens] = useState<SessionTokens | null>(() => readStoredTokens())
  const [me, setMe] = useState<AuthMePlayer | null>(null)
  const [organizations, setOrganizations] = useState<AuthOrganization[]>([])
  const [currentOrgId, setCurrentOrgIdState] = useState<string | null>(null)
  const [isResolvingProfile, setIsResolvingProfile] = useState(() => hasActiveAccessToken(tokens))
  // True until the initial /auth/refresh attempt resolves. While false, we
  // cannot tell whether the visitor has a session — show 'checking', not
  // 'unauthenticated', to avoid flashing the login screen for returning users.
  const [isInitialRefreshDone, setIsInitialRefreshDone] = useState(false)
  // True once loadProfileData has finished at least one round-trip for
  // the current session. Without this flag, hasSession flipping
  // false → true (after /auth/refresh) renders state='authenticated'
  // for one paint with me still null — because the loadProfileData
  // useEffect that would set isResolvingProfile=true hasn't run yet.
  // RequireAuth then fires a phantom deny → /forbidden. Gating
  // 'authenticated' on this flag closes that window.
  const [profileResolveAttempted, setProfileResolveAttempted] = useState(false)

  const hasSession = useMemo(() => hasActiveAccessToken(tokens), [tokens])

  const authState: AuthState = useMemo(() => {
    if (!isInitialRefreshDone) return 'checking'
    if (!hasSession) return 'unauthenticated'
    // Invariant: we never declare 'authenticated' while the profile
    // fetch hasn't at least attempted to complete for this session.
    // Even if me ends up null (e.g. /me/player real failure caught by
    // 7579323's safe fallback), profileResolveAttempted flips true in
    // the .finally — so we exit 'checking' instead of hanging.
    if (isResolvingProfile || !profileResolveAttempted) return 'checking'
    return 'authenticated'
  }, [hasSession, isInitialRefreshDone, isResolvingProfile, profileResolveAttempted])

  // Silent refresh on mount — restore the session via the HttpOnly refresh
  // cookie set during the previous login. If no cookie or expired, the call
  // returns null and the provider lands in 'unauthenticated' state.
  useEffect(() => {
    // Fresh OAuth login: /auth/callback carries a single-use `code` that
    // CallbackPage exchanges for the session; the tc_refresh cookie isn't set
    // yet, so /auth/refresh here is a guaranteed-null round-trip that also
    // forces an extra concurrent cold Lambda container right when the user is
    // waiting. Skip it — the code exchange establishes the session — and just
    // mark the initial refresh done so authState can leave 'checking'.
    if (typeof window !== 'undefined') {
      const { pathname, search } = window.location
      if (pathname.startsWith('/auth/callback') && new URLSearchParams(search).has('code')) {
        setIsInitialRefreshDone(true)
        return
      }
    }
    let cancelled = false
    void refreshSessionApi()
      .then((nextTokens) => {
        if (cancelled) return
        if (nextTokens?.accessToken) {
          writeStoredTokens(nextTokens)
          setTokens(readStoredTokens())
        } else {
          // Definitive "no session": drop the stale session hint so
          // route-entry prefetches treat this browser as anonymous.
          clearStoredTokens()
        }
      })
      .catch(() => {
        // Network or unexpected error — proceed as unauthenticated; user can
        // sign in manually.
      })
      .finally(() => {
        if (!cancelled) setIsInitialRefreshDone(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const setCurrentOrgId = useCallback(
    (orgId: string | null) => {
      const nextOrgId = String(orgId || '').trim()
      if (!nextOrgId) {
        setCurrentOrgIdState(null)
        return
      }
      const allowed = organizations.some((row) => row.orgId === nextOrgId)
      setCurrentOrgIdState(allowed ? nextOrgId : null)
    },
    [organizations],
  )

  const loadProfileData = useCallback(async (): Promise<AuthOrganization[]> => {
    const { me: meResult, organizations: normalizedOrgs } = await fetchAuthProfile()
    setMe(meResult || null)
    setOrganizations(normalizedOrgs)
    setCurrentOrgIdState((prev) => {
      if (prev && normalizedOrgs.some((row) => row.orgId === prev)) return prev
      return normalizedOrgs[0]?.orgId || null
    })
    // Return the resolved list so the direct-navigate login paths (password /
    // set-password) can pick the post-login landing without waiting for a
    // re-render to observe the new organizations state.
    return normalizedOrgs
  }, [])

  useEffect(() => {
    if (!hasSession) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMe(null)
      setOrganizations([])
      setCurrentOrgIdState(null)
      setIsResolvingProfile(false)
      // Sign-out / no-session: reset so the next sign-in goes back
      // through the 'checking' state until its profile resolves.
      setProfileResolveAttempted(false)
      return
    }

    let isMounted = true
    setIsResolvingProfile(true)
    // hasSession just flipped (or first mounted with valid tokens).
    // Reset the attempted flag so authState computes 'checking' until
    // the new fetch finishes — even if state was previously
    // 'authenticated' from a prior session in the same React tree.
    setProfileResolveAttempted(false)

    void loadProfileData()
      .catch(() => {
        if (!isMounted) return
        setMe(null)
        setOrganizations([])
        setCurrentOrgIdState(null)
      })
      .finally(() => {
        if (!isMounted) return
        setIsResolvingProfile(false)
        setProfileResolveAttempted(true)
      })

    return () => {
      isMounted = false
    }
  }, [hasSession, loadProfileData])

  const refreshProfile = useCallback(async (): Promise<void> => {
    if (!hasSession) return
    await loadProfileData()
  }, [hasSession, loadProfileData])

  const { isSuperAdmin, isClubStaff, isAnyClubStaff, canManageFollowers, canEditStaff, canManageClub } = useAuthPermissions({
    me,
    organizations,
    currentOrgId,
  })

  /**
   * Store a freshly-issued session and resolve its profile. Extracted because
   * every password-shaped door — signing in, activating via a set-password link,
   * finishing an invited sign-in, answering a two-factor code — issues its
   * session through one backend helper, so they must land in identical provider
   * state here too. Returns the resolved club list, which is what the forms hand
   * to resolveLoginLanding to pick the post-login landing by club count.
   */
  const adoptSession = useCallback(
    async (nextTokens: SessionTokens): Promise<AuthOrganization[]> => {
      writeStoredTokens(nextTokens)
      setTokens(readStoredTokens())
      setIsResolvingProfile(true)
      setProfileResolveAttempted(false)
      try {
        return await loadProfileData()
      } finally {
        setIsResolvingProfile(false)
        setProfileResolveAttempted(true)
      }
    },
    [loadProfileData],
  )

  // Those doors live in passwordSessionDoors; every one stores through adoptSession.
  const passwordDoors = passwordSessionDoors(adoptSession)

  async function login(returnTo = '/', options: AuthLoginOptions = {}): Promise<void> {
    const mode = options.mode || 'signin'
    // Google legitimately redirects through Cognito straight to the identity
    // provider — keep the OAuth authorize flow for it.
    if (mode === 'google') {
      await startSignInFlow(returnTo)
      return
    }
    // Email/password sign-in and sign-up must land on our branded React auth
    // pages, never the off-brand Cognito Hosted UI form (security-baseline §1).
    // AuthProvider lives outside the Router, so navigate via the browser; the
    // SPA boots into /login (or /register) and honours ?returnTo on success.
    const target = mode === 'signup' ? '/register' : '/login'
    window.location.assign(`${target}?returnTo=${encodeURIComponent(returnTo)}`)
  }

  // useCallback (stable identity) so consumers that depend on completeCallback
  // — notably CallbackPage's exchange effect — don't re-fire on every provider
  // re-render (the mount-time /auth/refresh always flips isInitialRefreshDone,
  // forcing one). Deps are empty: the body only touches module imports and the
  // stable setTokens setter.
  const completeCallback = useCallback(async (code: string, stateParam: string | null): Promise<string> => {
    const { tokens: nextTokens, returnTo } = await exchangeCodeForTokens(code, stateParam)
    writeStoredTokens(nextTokens)
    setTokens(readStoredTokens())
    return returnTo
  }, [])

  async function logout(): Promise<void> {
    // Clear the server-side cookie and revoke Cognito refresh tokens before
    // wiping local state. Best-effort: the server call is awaited but never
    // throws — the local session ends regardless.
    await logoutSessionApi()
    clearStoredTokens()
    setTokens(null)
    setMe(null)
    setOrganizations([])
    setCurrentOrgIdState(null)
    setProfileResolveAttempted(false)
    setIsResolvingProfile(false)
  }

  const value: AuthContextValue = {
    state: authState,
    tokens,
    me,
    organizations,
    currentOrgId,
    setCurrentOrgId,
    isSuperAdmin,
    isClubStaff,
    isAnyClubStaff,
    canManageFollowers,
    canEditStaff,
    canManageClub,
    ...passwordDoors,
    login,
    completeCallback,
    logout,
    refreshProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
